import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import type { UserPricingOverride } from '../../models/pricing-code-model';
import { formatDiscountLabel } from '../../models/pricing-group-model';

interface PricingOverrideBadgeProps {
  override: UserPricingOverride;
  compact?: boolean;
}

const OVERRIDE_COLORS: Record<string, string> = {
  free: '#10B981',
  percentage: '#3B82F6',
  flat_rate: '#F59E0B',
};

export default function PricingOverrideBadge({ override, compact = false }: PricingOverrideBadgeProps) {
  const color = OVERRIDE_COLORS[override.discount_type] ?? '#3B82F6';
  const label = formatDiscountLabel(override.discount_type, override.discount_value);
  const sourceLabel = override.source_type === 'group' ? 'via Group' : 'via Code';

  if (compact) {
    return (
      <View style={[styles.compactBadge, { backgroundColor: color + '20' }]}>
        <MaterialIcons name="local-offer" size={12} color={color} />
        <Text style={[styles.compactText, { color }]}>{label}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.iconContainer, { backgroundColor: color + '20' }]}>
        <MaterialIcons name="local-offer" size={20} color={color} />
      </View>

      <View style={styles.content}>
        <Text style={styles.label}>Your Rate</Text>
        <Text style={[styles.discountValue, { color }]}>{label}</Text>
      </View>

      <View style={styles.sourceInfo}>
        <Text style={styles.sourceType}>{sourceLabel}</Text>
        <Text style={styles.sourceName} numberOfLines={1}>
          {override.source_name}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#292D33',
    borderRadius: 10,
    padding: 12,
    marginHorizontal: 16,
    marginTop: 14,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  label: {
    color: '#8D8E90',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  discountValue: {
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  sourceInfo: {
    alignItems: 'flex-end',
  },
  sourceType: {
    color: '#8D8E90',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 2,
  },
  sourceName: {
    color: '#FFFFFF',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
    maxWidth: 120,
  },
  compactBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  compactText: {
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
});
